import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/authStore'
import type { PaginatedResponse, OrdemServico } from '../../types'
import OnboardingBanner from './OnboardingBanner'
import styles from './DashboardPage.module.css'

interface DashboardResumo {
  os_por_status: Record<string, number>
  sla: { total_mes: number; concluidas_no_prazo: number; compliance_pct: number }
  qai: { total_analises: number; conformes: number; conformidade_pct: number }
}

interface SlaVencimento {
  id: string
  tipo_os: string
  status: string
  data_prevista: string
  site: string | null
  dias_restantes: number | null
}

function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const [osResp, equipResp, pmocResp] = await Promise.all([
        api.get<PaginatedResponse<OrdemServico>>('/ordens-servico/', {
          params: { status: 'aberta,atribuida,em_execucao', page_size: 1 },
        }),
        api.get('/equipamentos/', { params: { page_size: 1 } }),
        api.get('/pmoc/', { params: { status: 'ativo', page_size: 1 } }),
      ])
      return {
        os_abertas: (osResp.data as PaginatedResponse<OrdemServico>).count,
        equipamentos_ativos: equipResp.data.count,
        pmocs_ativos: pmocResp.data.count,
      }
    },
    staleTime: 1000 * 60,
  })
}

function useOsRecentes() {
  return useQuery({
    queryKey: ['os-recentes'],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<OrdemServico>>('/ordens-servico/', {
        params: { ordering: '-aberta_em', page_size: 5 },
      })
      return r.data.results
    },
  })
}

function useSlaVencimento() {
  return useQuery({
    queryKey: ['sla-vencimento'],
    queryFn: async () => {
      const r = await api.get<SlaVencimento[]>('/analytics/sla-vencimento/', { params: { dias: 7 } })
      return r.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

function useDashboardResumo() {
  return useQuery({
    queryKey: ['dashboard-resumo'],
    queryFn: async () => {
      const r = await api.get<DashboardResumo>('/analytics/dashboard/')
      return r.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

const statusCor: Record<string, string> = {
  aberta: '#1565C0',
  atribuida: '#7B1FA2',
  em_execucao: '#F57C00',
  concluida: '#388E3C',
  aprovada: '#00897B',
  cancelada: '#616161',
}

export default function DashboardPage() {
  const user = useAuthStore((s) => s.user)
  const { data: stats, isLoading: loadingStats } = useDashboardStats()
  const { data: osRecentes, isLoading: loadingOs } = useOsRecentes()
  const { data: slaVencimento, isLoading: loadingSla } = useSlaVencimento()
  const { data: resumo, isLoading: loadingResumo } = useDashboardResumo()

  const qaiNaoConformes =
    resumo ? resumo.qai.total_analises - resumo.qai.conformes : 0

  return (
    <div className={styles.page} data-testid="dashboard-root">
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Dashboard</h1>
          <p className={styles.subtitle}>Bem-vindo, {user?.nomeCompleto?.split(' ')[0] ?? 'RT'}</p>
        </div>
      </header>

      <OnboardingBanner />

      <section className={styles.statsGrid} aria-label="Indicadores">
        <StatCard
          label="OS em Aberto"
          value={stats?.os_abertas ?? 0}
          icon="📋"
          loading={loadingStats}
          href="/ordens-servico"
          color="#1565C0"
          testId="dashboard-kpi-os-abertas"
        />
        <StatCard
          label="Equipamentos Ativos"
          value={stats?.equipamentos_ativos ?? 0}
          icon="🔧"
          loading={loadingStats}
          href="/equipamentos"
          color="#388E3C"
          testId="dashboard-kpi-equipamentos"
        />
        <StatCard
          label="PMOCs Ativos"
          value={stats?.pmocs_ativos ?? 0}
          icon="📋"
          loading={loadingStats}
          href="/pmoc"
          color="#7B1FA2"
          testId="dashboard-kpi-pmocs"
        />
        <StatCard
          label="SLA vencendo em 7 dias"
          value={slaVencimento?.length ?? 0}
          icon="⏰"
          loading={loadingSla}
          href="/ordens-servico"
          color="#F57C00"
          testId="dashboard-kpi-sla"
        />
        <StatCard
          label="Análises QAI não conformes"
          value={qaiNaoConformes}
          icon="⚠️"
          loading={loadingResumo}
          href="/relatorios"
          color="#D32F2F"
          testId="dashboard-kpi-qai"
        />
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>OS Recentes</h2>
          <Link to="/ordens-servico" className={styles.verTodas}>Ver todas →</Link>
        </div>
        {loadingOs ? (
          <div className={styles.loading}>Carregando...</div>
        ) : (
          <div className={styles.osList}>
            {osRecentes?.length === 0 && <p className={styles.emptyMsg}>Nenhuma OS encontrada.</p>}
            {osRecentes?.map((os) => (
              <Link key={os.id} to={`/ordens-servico/${os.id}`} className={styles.osCard}>
                <div className={styles.osInfo}>
                  <span className={styles.osTitulo}>{os.titulo}</span>
                  <span className={styles.osNumero}>OS #{os.numero}</span>
                </div>
                <div className={styles.osMeta}>
                  <span
                    className={styles.osStatus}
                    style={{ backgroundColor: statusCor[os.status] + '20', color: statusCor[os.status] }}
                  >
                    {os.status?.replace('_', ' ') ?? '—'}
                  </span>
                  <span
                    className={styles.osPrioridade}
                    style={{
                      color:
                        os.prioridade === 'urgente' || os.prioridade === 'emergencial'
                          ? '#D32F2F'
                          : '#757575',
                    }}
                  >
                    {os.prioridade}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function StatCard({
  label,
  value,
  icon,
  loading,
  href,
  color,
  testId,
}: {
  label: string
  value: number
  icon: string
  loading?: boolean
  href: string
  color: string
  testId?: string
}) {
  return (
    <Link
      to={href}
      className={styles.statCard}
      style={{ borderTopColor: color }}
      data-testid={testId}
      aria-label={`${label}: ${loading ? 'carregando' : value}`}
    >
      <div className={styles.statIcon} style={{ color }} aria-hidden="true">
        {icon}
      </div>
      <div className={styles.statValue} style={{ color }}>
        {loading ? '—' : value}
      </div>
      <div className={styles.statLabel}>{label}</div>
    </Link>
  )
}
